import useResizeObserver from '@react-hook/resize-observer';
import * as React from 'react';
import { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

const FlexSpaceAround = `
    display: flex;
    justify-content: space-around;
    flex-direction: column;
    transition: all 0.2s ease-in-out;
`;

export const ColumnLeft = styled.div`
	${FlexSpaceAround}
	position: absolute;
	left: -1.25em;
	transition: all 0.3s ease-in-out;
	background-color: ${props => props.theme.colors.grey?.[200]};
	height: 100%;
	opacity: 0;
	overflow: hidden;
	cursor: grab;
	&:active {
		cursor: grabbing;
	}
`;

export const ColumnCenter = styled.div<{ $canDrag?: boolean }>`
	${FlexSpaceAround}
	height: 100%;
	flex-grow: 1;
	cursor: ${props => (props.$canDrag ? 'grab' : 'default')};
	&:active {
		cursor: ${props => (props.$canDrag ? 'grabbing' : 'default')};
	}
`;

export const ColumnRight = styled.div<{ $openWidth?: string }>`
	${FlexSpaceAround}
	transition: all 0.3s ease-in-out;
	position: absolute;
	right: 0em;
	background-color: ${props => props.theme.colors.grey?.[200]};
	overflow: hidden;
	display: flex;
	padding-right: 0.25em;
`;
export const ColumnHeader = styled.div<{ $hideColumnBorders?: boolean; $backgroundColor?: string }>`
	background-color: ${props => props.$backgroundColor ?? props.theme.colors.grey?.[200]};
	border-right: ${props => (props.$hideColumnBorders ? null : '1px solid' + props.theme.colors.grey?.[300])};
	border-bottom: 1px solid ${props => props.theme.colors.grey?.[300]};
	height: 60px;
	width: 100%;
	min-height: 30px;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-around;
	overflow: visible;
	padding: 0.25em 0.5em;
	font-weight: 400;
`;

export const ColumnRoot = styled.div<{ $lockOpen?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: relative;
	width: 100%;
`;

export const RowFiller = styled.div<{$backgroundColour?: string, $isLastRow?: boolean}>`
	${props => !props.$isLastRow ? `border-bottom: 1px solid ${props.theme.colors.grey[300]};` : ''};
	background-color: ${props => props.$backgroundColour ?? props.theme.colors.white};
`;

export const Column = styled.div<{ $width?: React.ReactText; $hideColumnBorders?: boolean; $colBgColor?: string, $isLastRow?: boolean; }>`
	width: ${props => (props.$width ? (typeof props.$width === 'number' ? props.$width + 'px' : props.$width) : '100%')};
	min-width: ${props => (props.$width ? (typeof props.$width === 'number' ? props.$width + 'px' : props.$width) : '100%')};
	border-right: ${props => (props.$hideColumnBorders ? null : '1px solid' + props.theme.colors.grey?.[300])};
	${props => !props.$isLastRow ? `border-bottom: 1px solid ${props.theme.colors.grey[300]};` : ''};
	background-color: ${props => props.$colBgColor ?? props.theme.colors.white};
	display: flex;
	flex-direction: column;
	justify-content: space-around;
	overflow: visible;
	overflow-x: hidden;
	
	&.fixed-right {
		border-left: ${props => (props.$hideColumnBorders ? null : '1px solid' + props.theme.colors.grey?.[300])};
	}
	.left {
		//border-right: 2px solid ${props => props.theme.colors.grey?.[400]};
	}
`;

export const Cell = styled.div<{ $padCell?: boolean; $rowHeight?: string, $bgColour?: string }>`
	height: ${props => props.$rowHeight ?? '40px'};
	background-color: ${ props => props.$bgColour ?? ''};
	font-weight: 300;
	display: flex;
	justify-content: space-around;
	flex-direction: column;
	${props =>
		props.$padCell
			? css`
					padding-left: 0.5em;
					padding-right: 0.5em;
			  `
			: null}
	div,
	span,
	p,
	a {
		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: hidden;
	}
	text-overflow: ellipsis;
	overflow: hidden;
	&:focus {
		outline: none;
	}
`;

export const Row = styled.div`
	display: flex;
	background-color: ${props => props.theme.colors.white};
	&:hover {
		${Cell}, ${Column} {
			background-color: ${props => props.theme.colors.grey?.[100]};
		}
	}
	${Column}:last-of-type, ${ColumnHeader}:last-of-type {
		border-right: none;
		${Cell}:last-of-type {
			border-right: none;
		}
	}

	.clear-last-border {
		border-right: none;
		flex-grow: 1;
	}
`;

export const MeasuredRow = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((p, fr) => {
	const { children, ...props } = p;

	const localRef = React.useRef<HTMLDivElement>();
	const initRef = React.useRef(false);
	useResizeObserver(localRef, () => (typeof fr === 'function' ? fr(localRef.current) : (fr.current = localRef.current)));

	return (
		<Row
			{...props}
			ref={r => {
				localRef.current = r;
				if (!initRef.current) {
					typeof fr === 'function' ? fr(localRef.current) : (fr.current = localRef.current);
					// initRef.current = true;
				}
			}}
		>
			{children}
		</Row>
	);
});

export const ColumnsWrapper = styled.div`
	width: 100%;
	overflow: auto;
	${Row}:last-of-type {
		border-bottom: none;
		${Column} {
			border-bottom: none;
		}
	}
`;

export const HoverRowVisible = styled.div`
	transition: all 0.2s ease-in-out;
	${ColumnHeader} {
		background-color: unset;
	}
	opacity: 0.15;
	${Cell} {
		background-color: unset !important;
	}
	&:hover {
		opacity: 1;
		background-color: unset;
		transform: translateX(0px);
	}
	background-color: unset;
`;
